import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useGmailStatus } from '@/components/GmailBanner';
import SignatureEditor from '@/components/SignatureEditor';
import { formatInstant } from '@/lib/date';
import { CALLBACK_MESSAGES } from '@/lib/gmail-callback';

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { data: gmail } = useGmailStatus();

  // Surface the callback result once, then strip it from the URL so a reload
  // does not re-announce a connection that happened ten minutes ago.
  useEffect(() => {
    const code = params.get('gmail');
    if (!code) return;
    const message = CALLBACK_MESSAGES[code];
    if (message) (message.ok ? toast.success : toast.error)(message.text);
    params.delete('gmail');
    setParams(params, { replace: true });
    queryClient.invalidateQueries({ queryKey: ['gmail-status'] });
  }, [params, setParams, queryClient]);

  const { data: settings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const res = await client.api['user-settings'].me.$get();
      if (!res.ok) throw new Error('Failed to fetch settings');
      return res.json();
    },
  });

  // `signature` is deliberately absent: the plain-text twin is derived from the
  // HTML server-side, so a client that also sent `emailSignature` would be
  // fighting the server over the same column.
  const [form, setForm] = useState({ businessName: '', signatureHtml: '', noResponseDays: 10 });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings && !dirty) {
      setForm({
        businessName: settings.businessName ?? '',
        signatureHtml: settings.emailSignatureHtml ?? '',
        noResponseDays: settings.noResponseDays ?? 10,
      });
    }
  }, [settings, dirty]);

  const saveSettings = useMutation({
    mutationFn: async () => {
      const res = await client.api['user-settings'].me.$put({
        json: {
          businessName: form.businessName.trim() || null,
          emailSignatureHtml: form.signatureHtml.trim() || null,
          noResponseDays: form.noResponseDays,
        },
      });
      if (!res.ok) {
        // The 400 here is "that signature refers to an image we could not
        // find" — a sentence written for the planner, so it is shown as-is.
        const payload = (await res.json().catch(() => null)) as { error?: unknown } | null;
        const message = typeof payload?.error === 'string' ? payload.error : undefined;
        throw new Error(message ?? 'Failed to save');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-settings'] });
      setDirty(false);
      toast.success('Settings saved');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const connect = useMutation({
    mutationFn: async () => {
      const res = await client.api.gmail.connect.$post();
      if (!res.ok) throw new Error('Could not start the Google connection');
      return res.json();
    },
    // Full-page navigation, not a popup: Google blocks the consent screen in
    // many popup contexts and the failure mode is a blank window.
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const disconnect = useMutation({
    mutationFn: async () => {
      const res = await client.api.gmail.disconnect.$post();
      if (!res.ok) throw new Error('Failed to disconnect');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmail-status'] });
      toast.success('Gmail disconnected. Your labels were left in place.');
    },
  });

  const syncNow = useMutation({
    mutationFn: async () => {
      const res = await client.api.gmail.sync.$post();
      if (!res.ok) throw new Error('Sync failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmail-status'] });
      toast.success('Sync complete');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const setArchiveOnFile = useMutation({
    mutationFn: async (value: boolean) => {
      const res = await client.api.gmail.settings.$put({ json: { archiveOnFile: value } });
      if (!res.ok) throw new Error('Failed to save');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gmail-status'] }),
  });

  /**
   * `connected` only says a row exists. Once Google revokes the grant the row
   * stays and `syncStatus` goes to `disconnected`, so a page that reads the
   * first field alone draws a healthy mailbox over a dead token — a last-synced
   * time and a Check now button for a connection that cannot sync (EVE-65).
   */
  const signedOut = gmail?.connected === true && gmail.syncStatus === 'disconnected';

  /**
   * Shared by both states rather than duplicated into each: the way out of a
   * connection does not change because the token died.
   */
  const disconnectAction = gmail?.connected ? (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={disconnect.isPending}>
          Disconnect
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="display-tight text-2xl font-semibold">
            Disconnect {gmail.email}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {signedOut
              ? 'This removes the account and its cached mail. Reconnecting is enough to resume tracking — you only need this to move to a different mailbox. Your labels stay put.'
              : 'Replies stop being tracked until you reconnect. Your labels stay put.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{signedOut ? 'Keep the account' : 'Stay connected'}</AlertDialogCancel>
          <AlertDialogAction onClick={() => disconnect.mutate()}>Disconnect</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  ) : null;

  return (
    <div className="space-y-8">
      <h1 className="display-tight text-3xl font-semibold">Settings</h1>

      {/* ── Gmail ───────────────────────────────────────────────────────── */}
      <Card className="rounded-3xl bg-card shadow-ambient">
        <CardHeader className="pb-2">
          <h2 className="label-caps text-xs text-muted-foreground">Gmail</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          {gmail?.connected ? (
            <>
              {signedOut ? (
                /* The warm container, and not the destructive red: a revoked
                   grant is a connection that needs renewing, not a fault the
                   planner caused. Same tone the global banner uses for it, so
                   the two are recognisably one state. */
                <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-[var(--color-tertiary-container)] px-4 py-3">
                  <AlertTriangle className="size-5 shrink-0 text-[#5c4a2e]" />
                  <div className="min-w-48 flex-1">
                    <p className="text-sm font-medium">{gmail.email} is signed out</p>
                    <p className="text-xs text-[#5c4a2e]">
                      Google ended this connection — replies aren't being tracked until you
                      reconnect.
                    </p>
                  </div>
                  {/* The fix, in one click. `upsert` lands a reconnect on the
                      same account row, so the disconnect a planner used to
                      have to run first was never anything but a detour — and
                      an alarming one, since it also purges the cached mail. */}
                  <Button disabled={connect.isPending} onClick={() => connect.mutate()}>
                    {connect.isPending ? 'Opening Google…' : 'Reconnect'}
                  </Button>
                  {disconnectAction}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3">
                  <CheckCircle2 className="size-5 text-primary" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{gmail.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {gmail.syncStatus === 'backfilling'
                        ? 'Finishing setup…'
                        : gmail.lastSyncedAt
                          ? `Last synced ${formatInstant(gmail.lastSyncedAt)}`
                          : 'Not yet synced'}
                      {!gmail.hasReadAccess && ' · send-only'}
                    </p>
                  </div>
                  {/* Only offered while a token can honour it. Asking the
                      poller to run on a revoked grant can only fail, and an
                      affordance that always fails teaches a planner to keep
                      pressing it instead of reconnecting. */}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={syncNow.isPending}
                    onClick={() => syncNow.mutate()}
                  >
                    <RefreshCw className={`size-3.5 ${syncNow.isPending ? 'animate-spin' : ''}`} />
                    Check now
                  </Button>
                  {disconnectAction}
                </div>
              )}

              {/* Suppressed while signed out: `markDisconnected` stores
                  "Reconnect to resume syncing", which the row above has
                  already said with the button to do it next to the words. */}
              {gmail.syncError && !signedOut && (
                <p className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {gmail.syncError}
                </p>
              )}

              {/* The label names what the planner gets, not the Gmail verb that
                  delivers it: "archive" was heard as "filed away where I can't
                  reach it" — or as deleted — by a planner who had no reason to
                  know it only removes the Inbox label (EVE-77). The verb stays
                  in the line below, where there is room to say what it does.

                  Both states get a line, because the off state is the one that
                  needs explaining: a reply the app has already filed sitting in
                  the inbox looks like a sync that half-worked. */}
              <div className="flex items-start justify-between gap-4 border-t border-border pt-4">
                <div>
                  <p className="text-sm font-medium">Remove filed mail from your inbox</p>
                  <p id="archive-on-file-help" className="text-xs text-muted-foreground">
                    {gmail.archiveOnFile
                      ? "Filed mail is archived in Gmail — it leaves your inbox but stays under the wedding's label and in All Mail. Nothing is deleted."
                      : "Filed mail gets the wedding's label and stays in your inbox."}
                  </p>
                </div>
                <Switch
                  checked={gmail.archiveOnFile}
                  onCheckedChange={(v) => setArchiveOnFile.mutate(v)}
                  aria-label="Remove filed mail from your inbox"
                  aria-describedby="archive-on-file-help"
                />
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <Button disabled={connect.isPending} onClick={() => connect.mutate()}>
                {connect.isPending ? 'Opening Google…' : 'Connect Gmail'}
              </Button>
              <p className="text-xs text-muted-foreground">
                VendorOps reads, labels, and sends. It never deletes.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Identity ────────────────────────────────────────────────────── */}
      <Card className="rounded-3xl bg-card shadow-ambient">
        <CardHeader className="pb-2">
          <h2 className="label-caps text-xs text-muted-foreground">Your details</h2>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="businessName">Business name</Label>
            <Input
              id="businessName"
              value={form.businessName}
              placeholder="Wildwood Events"
              aria-describedby="businessName-hint"
              onChange={(e) => {
                setForm({ ...form, businessName: e.target.value });
                setDirty(true);
              }}
            />
            <p id="businessName-hint" className="text-xs text-muted-foreground">
              Fills <code className="text-xs">{'{{planner_business}}'}</code> in templates.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="signature-editor">Email signature</Label>
            <SignatureEditor
              value={form.signatureHtml}
              onChange={(html) => {
                setForm((current) => ({ ...current, signatureHtml: html }));
                setDirty(true);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="noResponseDays">Mark as no response after</Label>
            <div className="flex items-center gap-2">
              <Input
                id="noResponseDays"
                type="number"
                min={1}
                max={90}
                className="w-20"
                value={form.noResponseDays}
                onChange={(e) => {
                  setForm({ ...form, noResponseDays: Number(e.target.value) });
                  setDirty(true);
                }}
              />
              <span className="text-sm text-muted-foreground">days</span>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!dirty}
              onClick={() => {
                setForm({
                  businessName: settings?.businessName ?? '',
                  signatureHtml: settings?.emailSignatureHtml ?? '',
                  noResponseDays: settings?.noResponseDays ?? 10,
                });
                setDirty(false);
              }}
            >
              Reset
            </Button>
            <Button size="sm" disabled={!dirty || saveSettings.isPending} onClick={() => saveSettings.mutate()}>
              {saveSettings.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
